import { z } from 'zod';
import {
  SubscriptionStatus,
  MandateType,
  MandateStatus,
  PaymentStatus,
} from '../types/subscription/enums';

/**
 * Admin Subscription Filters Schema
 * Query parameters for GET /admin/subscriptions endpoint
 */
export const AdminSubscriptionFiltersSchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  status: z.array(z.nativeEnum(SubscriptionStatus)).optional(),
  planId: z.array(z.string().uuid()).optional(),
  paymentMethod: z.enum(['UPI', 'CARD', 'NONE']).optional(),
  vendorSearch: z.string().optional(),
  createdAfter: z.string().datetime().optional(),
  createdBefore: z.string().datetime().optional(),
  sortField: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type AdminSubscriptionFilters = z.infer<typeof AdminSubscriptionFiltersSchema>;

/**
 * Admin Subscription Row Schema
 * Single row in the subscription list table
 */
export const AdminSubscriptionRowSchema = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  vendorEmail: z.string().email(),
  vendorPhone: z.string().nullable(),
  planId: z.string().uuid(),
  planName: z.string(),
  status: z.nativeEnum(SubscriptionStatus),
  nextBillingDate: z.string().nullable(), // ISO date string
  mrr: z.number(),
  isOverridden: z.boolean(),
  paymentMethod: z.string().nullable(),
  createdAt: z.string(), // ISO date string
  trialEndsAt: z.string().nullable(), // ISO date string
  currentPeriodEnd: z.string().nullable(), // ISO date string
});
export type AdminSubscriptionRow = z.infer<typeof AdminSubscriptionRowSchema>;

/**
 * Admin Subscription List Response Schema
 * Paginated response with status counts
 */
export const AdminSubscriptionListResponseSchema = z.object({
  rows: z.array(AdminSubscriptionRowSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  statusCounts: z.record(z.nativeEnum(SubscriptionStatus), z.number()),
});
export type AdminSubscriptionListResponse = z.infer<typeof AdminSubscriptionListResponseSchema>;

/**
 * Payment history item for subscription detail
 */
export const AdminPaymentHistorySchema = z.object({
  id: z.string().uuid(),
  amount: z.number(),
  status: z.nativeEnum(PaymentStatus),
  paidAt: z.string().nullable(), // ISO date string
  billingPeriod: z.string(),
});
export type AdminPaymentHistory = z.infer<typeof AdminPaymentHistorySchema>;

/**
 * Mandate info for subscription detail
 */
export const AdminMandateSchema = z.object({
  type: z.nativeEnum(MandateType),
  status: z.nativeEnum(MandateStatus),
  maskedIdentifier: z.string(),
});
export type AdminMandate = z.infer<typeof AdminMandateSchema>;

/**
 * Audit trail entry for subscription detail
 */
export const AdminAuditTrailSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  performedBy: z.string().uuid(),
  performedByName: z.string(),
  createdAt: z.string(), // ISO date string
  metadata: z.record(z.unknown()).nullable(),
});
export type AdminAuditTrail = z.infer<typeof AdminAuditTrailSchema>;

/**
 * Admin Subscription Detail Schema
 * Full subscription detail with related data
 */
export const AdminSubscriptionDetailSchema = AdminSubscriptionRowSchema.extend({
  plan: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    monthlyPrice: z.number(),
    annualPrice: z.number(),
    features: z.array(
      z.object({
        id: z.string().uuid(),
        featureKey: z.string(),
      })
    ),
  }),
  vendor: z.object({
    name: z.string(),
    email: z.string().email(),
    phone: z.string().nullable(),
    gstin: z.string().nullable(),
    address: z.string().nullable(),
  }),
  payments: z.array(AdminPaymentHistorySchema),
  mandate: AdminMandateSchema.nullable(),
  auditTrail: z.array(AdminAuditTrailSchema),
});
export type AdminSubscriptionDetail = z.infer<typeof AdminSubscriptionDetailSchema>;

/**
 * Extend Trial Schema
 * Request body for PUT /admin/subscriptions/:id/extend-trial
 */
export const ExtendTrialSchema = z.object({
  days: z.number().int().positive(),
  reason: z.string().min(10).max(500),
});
export type ExtendTrialRequest = z.infer<typeof ExtendTrialSchema>;

/**
 * Create Comp Schema
 * Request body for POST /admin/subscriptions/comp
 */
export const CreateCompSchema = z
  .object({
    vendorId: z.string().uuid(),
    planId: z.string().uuid(),
    duration: z.enum(['FOREVER', 'CUSTOM']),
    customEndDate: z.string().datetime().optional(),
    reason: z.string().min(10).max(500),
  })
  .refine(
    (data) => {
      if (data.duration === 'CUSTOM') {
        return !!data.customEndDate;
      }
      return true;
    },
    {
      message: 'customEndDate is required when duration is CUSTOM',
      path: ['customEndDate'],
    }
  );
export type CreateCompRequest = z.infer<typeof CreateCompSchema>;

/**
 * Activate Subscription Schema
 * Request body for POST /admin/subscriptions/:id/activate
 */
export const ActivateSubscriptionSchema = z.object({
  reason: z.string().min(5).max(500),
  periodEndDate: z.string().datetime().optional(),
});
export type ActivateSubscriptionRequest = z.infer<typeof ActivateSubscriptionSchema>;

/**
 * Deactivate Subscription Schema
 * Request body for POST /admin/subscriptions/:id/deactivate
 */
export const DeactivateSubscriptionSchema = z.object({
  reason: z.string().min(5).max(500),
});
export type DeactivateSubscriptionRequest = z.infer<typeof DeactivateSubscriptionSchema>;

/**
 * Change Plan Schema
 * Request body for PATCH /admin/subscriptions/:id/plan
 */
export const ChangePlanSchema = z.object({
  planId: z.string().uuid(),
  reason: z.string().min(5).max(500),
});
export type ChangePlanRequest = z.infer<typeof ChangePlanSchema>;

/**
 * Admin Analytics Filters Schema
 * Query parameters for GET /admin/subscriptions/analytics endpoint
 */
export const AdminAnalyticsFiltersSchema = z
  .object({
    period: z.enum(['7d', '30d', '90d', '12m', 'custom']),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
  })
  .refine(
    (data) => {
      if (data.period === 'custom') {
        return !!data.startDate && !!data.endDate;
      }
      return true;
    },
    {
      message: 'startDate and endDate are required when period is custom',
    }
  );
export type AdminAnalyticsFilters = z.infer<typeof AdminAnalyticsFiltersSchema>;

/**
 * MRR Trend Point Schema
 * Single data point in the MRR trend chart
 */
export const MRRTrendPointSchema = z.object({
  month: z.string(), // Format: 'YYYY-MM'
  mrr: z.number(),
  subscriptionCount: z.number().int(),
});
export type MRRTrendPoint = z.infer<typeof MRRTrendPointSchema>;

/**
 * Plan Breakdown Schema
 * Revenue breakdown by subscription plan
 */
export const PlanBreakdownSchema = z.object({
  planId: z.string().uuid(),
  planName: z.string(),
  mrr: z.number(),
  subscriptionCount: z.number().int(),
  percentageOfTotal: z.number(),
  avgRevenuePerSub: z.number(),
});
export type PlanBreakdown = z.infer<typeof PlanBreakdownSchema>;

/**
 * Status Distribution Schema
 * Subscription counts by status
 */
export const StatusDistributionSchema = z.object({
  status: z.string(),
  count: z.number().int(),
  percentage: z.number(),
});
export type StatusDistribution = z.infer<typeof StatusDistributionSchema>;

/**
 * Monthly Metrics Schema
 * Historical monthly metrics
 */
export const MonthlyMetricsSchema = z.object({
  month: z.string(), // Format: 'YYYY-MM'
  churnRate: z.number(),
  conversionRate: z.number(),
  newSubscriptions: z.number().int(),
  canceledSubscriptions: z.number().int(),
});
export type MonthlyMetrics = z.infer<typeof MonthlyMetricsSchema>;

/**
 * MRR Analytics Response Schema
 * Complete analytics response with all metrics
 */
export const MRRAnalyticsResponseSchema = z.object({
  totalMRR: z.number(),
  activeCount: z.number().int(),
  trialCount: z.number().int(),
  churnRate: z.number(),
  conversionRate: z.number(),
  mrrChange: z.number(),
  mrrTrend: z.array(MRRTrendPointSchema),
  planBreakdown: z.array(PlanBreakdownSchema),
  statusDistribution: z.array(StatusDistributionSchema),
  monthlyMetrics: z.array(MonthlyMetricsSchema),
  period: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});
export type MRRAnalyticsResponse = z.infer<typeof MRRAnalyticsResponseSchema>;
